// Foreground browser notifications.
//
// We deliberately don't use a service worker / web push — the kanban is
// a desktop tool that's always open in a tab. Native Notification API
// fires fine when the tab is in another window or minimised, which is
// the only case we care about ("notify me when something changes while
// I'm not looking at this tab").
//
// Page Visibility API (document.hidden) gates whether to fire — there's
// no point flashing an OS notification when the tab is already focused
// (the in-app banner already handles that case).
//
// Per-user opt-in is stored in localStorage so it persists across
// sessions but never leaks across machines.

const STORAGE_KEY = "breeze:notifyEnabled";

export function isSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function getPermission(): NotificationPermission | "unsupported" {
  if (!isSupported()) return "unsupported";
  return Notification.permission;
}

export function isEnabled(): boolean {
  if (!isSupported()) return false;
  if (Notification.permission !== "granted") return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// Request permission. Returns the resulting NotificationPermission.
// Caller should flip the toggle ON only if this resolves to "granted".
export async function requestPermission(): Promise<NotificationPermission> {
  if (!isSupported()) return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return Notification.requestPermission();
}

export function setEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    /* localStorage unavailable — silently degrade */
  }
}

// Fire a notification IF:
//   - browser supports it
//   - user has granted permission
//   - user has flipped the in-app toggle on
//   - tab is currently hidden (otherwise the in-app banner is enough)
//
// Returns true if a notification was actually fired, false if suppressed.
export function notify(
  title: string,
  options: NotificationOptions & { onClick?: () => void } = {}
): boolean {
  if (!isEnabled()) return false;
  if (typeof document !== "undefined" && !document.hidden) return false;
  try {
    const { onClick, ...rest } = options;
    const n = new Notification(title, {
      icon: "/favicon.ico",
      ...rest,
    });
    if (onClick) {
      n.onclick = (e): void => {
        e.preventDefault();
        window.focus();
        try {
          onClick();
        } catch {
          /* ignore */
        }
        n.close();
      };
    }
    return true;
  } catch {
    return false;
  }
}
